/**
 * 联系人表单弹窗（新建/编辑）+ 农历生日输入组件。
 * 请求体严格按 CreateContactBody / UpdateContactBody 白名单拼装。
 */
import { useEffect, useState } from 'react';
import { useCreateContact, useUpdateContact } from '../hooks/useContacts';
import type { Contact, CreateContactBody, UpdateContactBody } from '../lib/types';
import { Button, ErrorBox, Field, Input, Modal, Select, Textarea, Toggle, errMsg } from './ui';

/** 农历生日输入：isLunarBirthday 切换；农历时选择月/日，公历时选择日期 */
export function LunarBirthdayInput({
  isLunar,
  onIsLunarChange,
  birthday,
  onBirthdayChange,
  lunarMonth,
  onLunarMonthChange,
  lunarDay,
  onLunarDayChange,
}: {
  isLunar: boolean;
  onIsLunarChange: (v: boolean) => void;
  birthday: string;
  onBirthdayChange: (v: string) => void;
  lunarMonth: string;
  onLunarMonthChange: (v: string) => void;
  lunarDay: string;
  onLunarDayChange: (v: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">农历生日</span>
        <Toggle checked={isLunar} onChange={onIsLunarChange} label={isLunar ? '农历' : '公历'} />
      </div>
      {isLunar ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="农历月">
            <Select value={lunarMonth} onChange={(e) => onLunarMonthChange(e.target.value)}>
              <option value="">选择月份</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m} 月
                </option>
              ))}
            </Select>
          </Field>
          <Field label="农历日">
            <Select value={lunarDay} onChange={(e) => onLunarDayChange(e.target.value)}>
              <option value="">选择日期</option>
              {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d} 日
                </option>
              ))}
            </Select>
          </Field>
        </div>
      ) : (
        <Field label="生日">
          <Input type="date" value={birthday} onChange={(e) => onBirthdayChange(e.target.value)} />
        </Field>
      )}
    </div>
  );
}

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  contact?: Contact | null; // 传入为编辑模式
}

const emptyForm = {
  name: '',
  company: '',
  position: '',
  phone: '',
  email: '',
  wechat: '',
  birthday: '',
  isLunar: false,
  lunarMonth: '',
  lunarDay: '',
  howMet: '',
  notes: '',
  intimacyLevel: '3',
  contactIntervalDays: '',
  isFavorite: false,
};

export function ContactForm({ open, onClose, contact }: ContactFormProps) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();

  useEffect(() => {
    if (open) {
      setForm(
        contact
          ? {
              name: contact.name,
              company: contact.company ?? '',
              position: contact.position ?? '',
              phone: contact.phone ?? '',
              email: contact.email ?? '',
              wechat: contact.wechat ?? '',
              birthday: contact.isLunarBirthday ? '' : (contact.birthday ?? ''),
              isLunar: contact.isLunarBirthday,
              lunarMonth: contact.lunarMonth ? String(contact.lunarMonth) : '',
              lunarDay: contact.lunarDay ? String(contact.lunarDay) : '',
              howMet: contact.howMet ?? '',
              notes: contact.notes ?? '',
              intimacyLevel: String(contact.intimacyLevel),
              contactIntervalDays: contact.contactIntervalDays ? String(contact.contactIntervalDays) : '',
              isFavorite: contact.isFavorite,
            }
          : emptyForm,
      );
      setError(null);
    }
  }, [open, contact]);

  const set = (patch: Partial<typeof emptyForm>) => setForm((f) => ({ ...f, ...patch }));

  async function submit() {
    if (!form.name.trim()) {
      setError('请填写姓名');
      return;
    }
    if (form.isLunar && (!form.lunarMonth || !form.lunarDay)) {
      setError('请选择完整的农历生日');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (contact) {
        // 编辑：字段整体提交（可清空用 null）
        const body: UpdateContactBody = { name: form.name.trim() };
        body.company = form.company.trim() || null;
        body.position = form.position.trim() || null;
        body.phone = form.phone.trim() || null;
        body.email = form.email.trim() || null;
        body.wechat = form.wechat.trim() || null;
        body.isLunarBirthday = form.isLunar;
        if (form.isLunar) {
          body.lunarMonth = form.lunarMonth ? Number(form.lunarMonth) : null;
          body.lunarDay = form.lunarDay ? Number(form.lunarDay) : null;
          body.birthday = null;
        } else {
          body.birthday = form.birthday || null;
          body.lunarMonth = null;
          body.lunarDay = null;
        }
        body.howMet = form.howMet.trim() || null;
        body.notes = form.notes.trim() || null;
        body.intimacyLevel = Number(form.intimacyLevel);
        body.contactIntervalDays = form.contactIntervalDays ? Number(form.contactIntervalDays) : null;
        body.isFavorite = form.isFavorite;
        await updateContact.mutateAsync({ id: contact.id, body });
      } else {
        // 新建：仅带已填字段（严格白名单）
        const body: CreateContactBody = { name: form.name.trim() };
        if (form.company.trim()) body.company = form.company.trim();
        if (form.position.trim()) body.position = form.position.trim();
        if (form.phone.trim()) body.phone = form.phone.trim();
        if (form.email.trim()) body.email = form.email.trim();
        if (form.wechat.trim()) body.wechat = form.wechat.trim();
        body.isLunarBirthday = form.isLunar;
        if (form.isLunar) {
          body.lunarMonth = Number(form.lunarMonth);
          body.lunarDay = Number(form.lunarDay);
        } else if (form.birthday) {
          body.birthday = form.birthday;
        }
        if (form.howMet.trim()) body.howMet = form.howMet.trim();
        if (form.notes.trim()) body.notes = form.notes.trim();
        body.intimacyLevel = Number(form.intimacyLevel);
        if (form.contactIntervalDays) body.contactIntervalDays = Number(form.contactIntervalDays);
        body.isFavorite = form.isFavorite;
        await createContact.mutateAsync(body);
      }
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contact ? '编辑联系人' : '新建联系人'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button onClick={submit} loading={submitting}>
            {contact ? '保存' : '创建'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBox error={error} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="姓名" required className="col-span-2">
            <Input autoFocus value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="公司">
            <Input value={form.company} onChange={(e) => set({ company: e.target.value })} />
          </Field>
          <Field label="职位">
            <Input value={form.position} onChange={(e) => set({ position: e.target.value })} />
          </Field>
          <Field label="电话">
            <Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="邮箱">
            <Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          </Field>
          <Field label="微信">
            <Input value={form.wechat} onChange={(e) => set({ wechat: e.target.value })} />
          </Field>
          <Field label="亲密度 (1-5)">
            <Select value={form.intimacyLevel} onChange={(e) => set({ intimacyLevel: e.target.value })}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="联系提醒间隔（天）">
            <Input
              type="number"
              min={0}
              value={form.contactIntervalDays}
              onChange={(e) => set({ contactIntervalDays: e.target.value })}
            />
          </Field>
          <div className="col-span-2">
            <LunarBirthdayInput
              isLunar={form.isLunar}
              onIsLunarChange={(v) => set({ isLunar: v })}
              birthday={form.birthday}
              onBirthdayChange={(v) => set({ birthday: v })}
              lunarMonth={form.lunarMonth}
              onLunarMonthChange={(v) => set({ lunarMonth: v })}
              lunarDay={form.lunarDay}
              onLunarDayChange={(v) => set({ lunarDay: v })}
            />
          </div>
          <Field label="相识方式" className="col-span-2">
            <Input value={form.howMet} onChange={(e) => set({ howMet: e.target.value })} />
          </Field>
          <Field label="备注" className="col-span-2">
            <Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
          </Field>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">收藏</span>
          <Toggle checked={form.isFavorite} onChange={(v) => set({ isFavorite: v })} label="特别关注" />
        </div>
      </div>
    </Modal>
  );
}
